/**
 * Die **Versionshistorie** als Seite (Spec v1.3, Stories 281 bis 283; ADR-0029).
 *
 * **Auf dem Bildschirm heißt sie „Neuerungen"** (#158): Er beantwortet, was sich
 * geändert hat. Nicht „Versionsverlauf", und nicht „Revision" — das Wort ist in
 * diesem Produkt als Fachbegriff vergeben und darf für nichts anderes stehen.
 * Der Bezeichner bleibt `version-history`, hier wie in der Adresse: Sichtbarer
 * Text ist deutsch, der Bezeichner englisch (ADR-0009).
 *
 * Je Fassung ein Abschnitt über die volle Breite, neueste oben, mit Fassung,
 * Datum und der Zahl **aller** Tickets im Kopf; darunter der Aufmacher und die
 * Einträge. Die Fassung, die auf dieser Instanz läuft, ist gekennzeichnet.
 *
 * **Flächig und nicht als Karte** (`ListPage`/`ListSection` wie Revision, Bestand
 * und Verwaltung): Ein Fassungstext ist Fließtext über die Breite der Ansicht und
 * kein Kästchen neben anderen Kästchen. Kein Aufklappen und kein Filter — es sind
 * fünf Fassungen, und wer sie liest, liest sie.
 *
 * **Die Ticketverweise stehen abgesetzt unter dem Eintrag** und nicht im Text
 * (ADR-0029): Der Text ist Nutzersprache, die Nummer ist der Beleg. Ohne
 * eingestellte Instanz des Ticketsystems steht die Nummer ohne Verweis da — ein
 * geratener führte irgendwohin.
 *
 * **Die Zahl der Tickets im Kopf ist die Kehrseite der Auslassung**: Ein Eintrag
 * nennt nur, was ein Nutzer künftig anders sieht; damit die Auslassung nicht
 * heimlich ist, steht daneben, wie viel in der Fassung insgesamt steckte.
 *
 * **Ohne Werkzeugleiste** (#41, #136): Sie zeigt an und bietet nichts zum
 * Handeln — kein Filter, kein Aufklappen, kein Knopf auf den Screen
 * (`docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`).
 */

import type { ReactElement } from "react";

import { Chip, StatusPill } from "../design-system";
import type { Version, VersionEntry, VersionHistory, VersionsApi } from "../api/client";
import { formatDate, formatNumber } from "../formatting";
import { ListPage, ListSection } from "./list";
import { Notice, useLoaded } from "./loading";
import { blocksOf, issueAddress } from "./versions";

const EXPLANATION =
  "Was sich mit jeder ausgelieferten Fassung dieser Anwendung geändert hat, aus Sicht " +
  "dessen, der sie benutzt. Die Zahl am Fassungskopf nennt alle Tickets, die in der " +
  "Fassung steckten — auch die, die hier keinen Eintrag haben, weil sie nichts Sichtbares " +
  "verändert haben.";

const NOTHING = "Zu dieser Anwendung ist noch kein Fassungstext hinterlegt.";

export function VersionHistoryView({ client }: { client: VersionsApi }): ReactElement {
  const { state } = useLoaded(() => client.versions());
  const history: VersionHistory | null = state.kind === "loaded" ? state.value : null;

  return (
    <ListPage>
      <ListSection title="Neuerungen" glyph="history" first>
        <div style={{ padding: "0 var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {history !== null && history.versions.length === 0 ? <Notice text={NOTHING} /> : null}
        </div>

        <p
          style={{
            margin: 0,
            padding: "0 var(--sp-5) var(--sp-5)",
            maxWidth: "78ch",
            fontSize: "var(--fs-11)",
            lineHeight: "var(--lh-normal)",
            color: "var(--fg-6)",
            textWrap: "pretty",
          }}
        >
          {EXPLANATION}
        </p>
      </ListSection>

      {(history?.versions ?? []).map((version) => (
        <VersionSection
          key={version.version}
          version={version}
          running={version.version === history?.running}
          issueBase={history?.issueBaseAddress ?? null}
        />
      ))}
    </ListPage>
  );
}

/**
 * Ein Abschnitt je Fassung.
 *
 * Die laufende Fassung trägt die Marke „diese Instanz" **und** eine leicht
 * getönte Fläche: Die Marke allein steht im Kopf und ist beim Herunterscrollen
 * weg, die Tönung trägt bis zum Ende des Abschnitts.
 */
function VersionSection({
  version,
  running,
  issueBase,
}: {
  version: Version;
  running: boolean;
  issueBase: string | null;
}): ReactElement {
  return (
    <div
      style={{
        background: running ? "color-mix(in srgb, var(--ac-blue) 6%, transparent)" : undefined,
      }}
    >
      <ListSection
        title={version.version}
        glyph="history"
        headerRight={
          <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
            {version.note === null ? null : <Chip>{version.note}</Chip>}
            {running ? <StatusPill tone="blue">diese Instanz</StatusPill> : null}
            <span
              style={{
                fontSize: "var(--fs-11)",
                color: "var(--fg-6)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatDate(version.date)}
            </span>
            <Chip>{ticketCount(version.tickets)}</Chip>
          </span>
        }
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-6)",
            padding: "0 var(--sp-5) var(--sp-8)",
          }}
        >
          {version.entries.map((entry, index) => (
            <EntryText
              key={entry.heading ?? `aufmacher-${String(index)}`}
              entry={entry}
              issueBase={issueBase}
            />
          ))}
        </div>
      </ListSection>
    </div>
  );
}

/**
 * Ein Eintrag: Überschrift, Text, Ticketmarken.
 *
 * Der **Aufmacher** hat keine Überschrift und steht deshalb etwas größer da — er
 * ist die Einordnung der Fassung und keine ihrer Neuerungen.
 */
function EntryText({
  entry,
  issueBase,
}: {
  entry: VersionEntry;
  issueBase: string | null;
}): ReactElement {
  const opener = entry.heading === null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
      {entry.heading === null ? null : (
        <h3
          style={{
            margin: 0,
            fontSize: "var(--fs-13)",
            fontWeight: "var(--fw-semibold)",
            color: "var(--fg-1)",
          }}
        >
          {entry.heading}
        </h3>
      )}

      {blocksOf(entry.markdown).map((block, index) =>
        block.kind === "paragraph" ? (
          <p key={index} style={textStyle(opener)}>
            {block.text}
          </p>
        ) : (
          <ul key={index} style={{ ...textStyle(opener), paddingLeft: "var(--sp-6)" }}>
            {block.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ),
      )}

      <TicketMarks tickets={entry.tickets} issueBase={issueBase} />
    </section>
  );
}

/**
 * Die Ticketverweise eines Eintrags — abgesetzt darunter, klickbar, wo eine
 * Instanz eingestellt ist.
 *
 * Keine Verweise heißt: **nichts**. Ein Eintrag ohne Ticket ist keine Lücke —
 * der Aufmacher hat nie eines —, und eine Zeile, die das anmerkte, wäre Lärm.
 */
function TicketMarks({
  tickets,
  issueBase,
}: {
  tickets: number[];
  issueBase: string | null;
}): ReactElement | null {
  if (tickets.length === 0) {
    return null;
  }

  return (
    <span
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "var(--sp-4)",
        fontSize: "var(--fs-11)",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {tickets.map((number) => {
        const label = `#${String(number)}`;
        const address = issueAddress(issueBase, number);

        return address === null ? (
          // Ohne Instanz kein Verweis — die Nummer bleibt trotzdem stehen: Sie
          // ist der Beleg, und der gilt auch ohne Weg dorthin.
          <span key={number} style={{ color: "var(--fg-6)" }}>
            {label}
          </span>
        ) : (
          <a key={number} href={address} target="_blank" rel="noreferrer">
            {label}
          </a>
        );
      })}
    </span>
  );
}

function textStyle(opener: boolean): Record<string, string> {
  return {
    margin: "0",
    maxWidth: "78ch",
    fontSize: opener ? "var(--fs-13)" : "var(--fs-12)",
    lineHeight: "var(--lh-normal)",
    color: opener ? "var(--fg-3)" : "var(--text-body)",
    textWrap: "pretty",
  };
}

function ticketCount(tickets: number): string {
  return `${formatNumber(tickets)} ${tickets === 1 ? "Ticket" : "Tickets"}`;
}
